import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Lightning, Plug, CheckCircle, XCircle, FloppyDisk, Shield, Key, Globe, Eye, EyeSlash,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const AUTH_LABELS = { api_key: "API Key", oauth2: "OAuth 2.0", basic: "Basic Auth", bearer: "Bearer Token", mtls: "mTLS" };

export default function IntegrationConfigDialog({ integration, open, onOpenChange, onSaved }) {
  const [spec, setSpec] = useState(null);
  const [authType, setAuthType] = useState("api_key");
  const [config, setConfig] = useState({});
  const [headers, setHeaders] = useState([]);
  const [showSecrets, setShowSecrets] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!integration || !open) return;
    setTestResult(null);
    api.get(`/integrations/${integration.id}/config`).then(({ data }) => {
      setSpec(data.spec);
      setAuthType(data.integration.auth_type || (data.spec.auth_types?.[0] || "api_key"));
      const cfg = { ...data.config };
      // Fill defaults
      (data.spec.fields || []).forEach((f) => {
        if (cfg[f.name] === undefined && f.default) cfg[f.name] = f.default;
      });
      setConfig(cfg);
      setHeaders(Object.entries(cfg.headers || {}).map(([k, v]) => ({ k, v })));
    });
  }, [integration, open]);

  const setField = (name, value) => setConfig({ ...config, [name]: value });
  const toggleSecret = (name) => setShowSecrets({ ...showSecrets, [name]: !showSecrets[name] });

  const addHeader = () => setHeaders([...headers, { k: "", v: "" }]);
  const rmHeader = (i) => setHeaders(headers.filter((_, idx) => idx !== i));

  const composedConfig = () => {
    const h = {};
    headers.forEach((x) => { if (x.k) h[x.k] = x.v; });
    return { ...config, headers: h };
  };

  const doTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/integrations/${integration.id}/test`, {
        auth_type: authType,
        config: composedConfig(),
      });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: "Test request failed" });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/integrations/${integration.id}/config`, {
        auth_type: authType, config: composedConfig(),
      });
      // If not yet connected, mark it so
      if (integration.status !== "connected") {
        await api.patch(`/integrations/${integration.id}`, { status: "connected" });
      }
      toast.success("Configuration saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!integration) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-0 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center">
              <Plug size={14} weight="fill" className="text-white" />
            </div>
            {integration.provider}
            <span className="chip chip-neutral ms-2">{integration.category}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-5 mt-2">
          {spec && (
            <>
              {/* Auth type */}
              <div>
                <Label className="text-xs text-[var(--text-2)] flex items-center gap-1 mb-2"><Shield size={10} /> Authentication method</Label>
                <Select value={authType} onValueChange={setAuthType}>
                  <SelectTrigger data-testid="integration-auth-type" className="glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(spec.auth_types || ["api_key"]).map((t) => (
                      <SelectItem key={t} value={t}>{AUTH_LABELS[t] || t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Provider-specific fields */}
              <div className="space-y-3">
                <div className="overline flex items-center gap-1"><Key size={10} /> Connection details</div>
                {(spec.fields || []).map((f) => {
                  const isSecret = f.type === "password";
                  const show = showSecrets[f.name];
                  return (
                    <div key={f.name}>
                      <Label className="text-xs text-[var(--text-2)]">{f.label} {f.required && <span className="text-[var(--danger)]">*</span>}</Label>
                      <div className="relative mt-1.5">
                        <Input
                          type={isSecret && !show ? "password" : "text"}
                          value={config[f.name] ?? ""}
                          onChange={(e) => setField(f.name, e.target.value)}
                          placeholder={f.placeholder || ""}
                          data-testid={`integration-field-${f.name}`}
                          className={`glass-subtle border-[var(--border)] font-mono text-xs ${isSecret ? "pr-9" : ""}`}
                        />
                        {isSecret && (
                          <button
                            type="button"
                            onClick={() => toggleSecret(f.name)}
                            className="absolute end-2 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-[var(--text)] p-1"
                            data-testid={`toggle-secret-${f.name}`}
                          >
                            {show ? <EyeSlash size={12} /> : <Eye size={12} />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom headers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-[var(--text-2)] flex items-center gap-1"><Globe size={10} /> Custom headers</Label>
                  <button onClick={addHeader} data-testid="add-header-btn" className="text-xs text-[var(--accent)] hover:underline">+ Add</button>
                </div>
                {headers.length === 0 && <div className="text-xs text-[var(--text-3)]">None</div>}
                <div className="space-y-2">
                  {headers.map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={h.k} onChange={(e) => { const nh = [...headers]; nh[i].k = e.target.value; setHeaders(nh); }} placeholder="Header-Name" data-testid={`header-key-${i}`} className="glass-subtle border-[var(--border)] font-mono text-xs" />
                      <Input value={h.v} onChange={(e) => { const nh = [...headers]; nh[i].v = e.target.value; setHeaders(nh); }} placeholder="value" data-testid={`header-val-${i}`} className="glass-subtle border-[var(--border)] font-mono text-xs" />
                      <Button variant="ghost" size="icon" onClick={() => rmHeader(i)} data-testid={`header-rm-${i}`}>×</Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Encryption / TLS */}
              <div className="flex items-center justify-between py-2 border-t border-[var(--border)]">
                <div>
                  <div className="text-xs font-medium flex items-center gap-1"><Shield size={10} className="text-emerald-400" /> Verify TLS certificates</div>
                  <div className="text-[10px] text-[var(--text-3)]">Reject self-signed certs. Keep enabled unless connecting to an internal dev server.</div>
                </div>
                <Switch checked={config.verify_tls !== false} onCheckedChange={(v) => setField("verify_tls", v)} data-testid="verify-tls-switch" />
              </div>

              {/* Test result */}
              {testResult && (
                <div className={`p-4 rounded-md border ${testResult.ok ? "bg-emerald-500/10 border-emerald-500/40" : "bg-rose-500/10 border-rose-500/40"}`} data-testid="test-result">
                  <div className="flex items-center gap-2 mb-1">
                    {testResult.ok ? <CheckCircle size={14} weight="fill" className="text-emerald-400" /> : <XCircle size={14} weight="fill" className="text-rose-400" />}
                    <div className="text-sm font-semibold">{testResult.ok ? "Connected" : "Connection failed"}</div>
                    {testResult.latency_ms != null && <span className="font-mono text-xs text-[var(--text-3)] ms-auto">{testResult.latency_ms}ms</span>}
                  </div>
                  <div className="text-xs text-[var(--text-2)]">{testResult.message}</div>
                  {testResult.details && Object.keys(testResult.details).length > 0 && (
                    <pre className="mt-2 text-[10px] font-mono text-[var(--text-3)] bg-[var(--surface)] p-2 rounded overflow-x-auto">{JSON.stringify(testResult.details, null, 2)}</pre>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 pt-4 border-t border-[var(--border)] mt-2">
          <Button variant="outline" onClick={doTest} disabled={testing} data-testid="test-connection-btn" className="gap-2 glass-subtle border-[var(--border-strong)]">
            <Lightning size={14} /> {testing ? "Testing…" : "Test connection"}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-integration-btn" className="ms-auto gap-2">
            <FloppyDisk size={14} /> {saving ? "Saving…" : "Save & connect"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
